import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { HEALTH_SERVICE } from '../config/defaults';

export interface ServiceHealthStatus {
  service: string;
  status: 'healthy' | 'unhealthy' | 'configured';
  type?: string;
  queueName?: string;
}

export interface HealthCheckResponse {
  status: 'healthy' | 'unhealthy' | 'degraded';
  duration: string;
  services: ServiceHealthStatus[];
  summary: {
    total: number;
    healthy: number;
    unhealthy: number;
  };
  runtime?: {
    uptime: string;
    memory: {
      used: string;
      total: string;
    };
  };
}

/**
 * Service for backend health monitoring and keep-alive
 */
class HealthService extends BaseService {
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private lastHealthCheck: HealthCheckResponse | null = null;
  private healthCheckCallbacks: ((health: HealthCheckResponse) => void)[] = [];
  private lastPrewarmTime = 0;
  private readonly HEALTH_CHECK_INTERVAL = HEALTH_SERVICE.CHECK_INTERVAL;
  private readonly IDLE_THRESHOLD = HEALTH_SERVICE.IDLE_THRESHOLD;
  // Optional throttle between prewarm requests. If not configured, prewarm every time.
  private readonly PREWARM_THROTTLE =
    (HEALTH_SERVICE as { PREWARM_THROTTLE?: number }).PREWARM_THROTTLE;

  constructor() {
    super('HealthService', {
      enableCircuitBreaker: true,
      enableRetry: true,
      maxRetryAttempts: HEALTH_SERVICE.MAX_RETRY_ATTEMPTS,
      timeout: HEALTH_SERVICE.REQUEST_TIMEOUT
    });
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, { attempt, delay, error: error.message });
  }

  /**
   * Initialize health monitoring
   * Call on app startup
   */
  async initialize(): Promise<void> {
    return this.executeOperation(async () => {
      // Try initial prewarm + health check so the heavy processChatWork/Foundry
      // path starts spinning up while the user reads the screen and starts typing.
      try {
        await this.prewarm();
      } catch (error) {
        console.log('Initial prewarm skipped (auth not ready yet)');
      }

      // Start periodic health checks
      this.startPeriodicHealthChecks();

      // Monitor user activity for pre-warming
      this.monitorUserActivity();
    }, 'initialize');
  }

  /**
   * Perform health check
   */
  async performHealthCheck(): Promise<HealthCheckResponse> {
    return this.executeOperation(async () => {
      const response = await apiService.get<HealthCheckResponse>('/api/system/keep-alive');
      this.lastHealthCheck = response;
      
      // Notify listeners
      this.healthCheckCallbacks.forEach(callback => callback(response));
      
      return response;
    }, 'performHealthCheck');
  }

  /**
   * Get last health check result
   */
  getLastHealthCheck(): HealthCheckResponse | null {
    return this.lastHealthCheck;
  }

  /**
   * Subscribe to health check updates
   */
  subscribe(callback: (health: HealthCheckResponse) => void): () => void {
    this.healthCheckCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.healthCheckCallbacks = this.healthCheckCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Start periodic health checks
   */
  private startPeriodicHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck().catch(error => {
        console.error('Periodic health check failed:', error);
      });
    }, this.HEALTH_CHECK_INTERVAL);
  }

  /**
   * Monitor user activity for pre-warming after idle periods
   */
  private monitorUserActivity(): void {
    let lastActivityTime = Date.now();
    let wasIdle = false;

    const resetActivityTimer = () => {
      const now = Date.now();
      const idleTime = now - lastActivityTime;

      if (wasIdle && idleTime < this.IDLE_THRESHOLD) {
        // User became active after idle period - pre-warm backend (incl. heavy path)
        console.log('User active after idle period, pre-warming backend...');
        this.prewarm().catch(error => {
          console.error('Pre-warm health check failed:', error);
        });
        wasIdle = false;
      }

      if (idleTime >= this.IDLE_THRESHOLD) {
        wasIdle = true;
      }

      lastActivityTime = now;
    };

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetActivityTimer, { passive: true });
    });
  }

  /**
   * Stop health monitoring
   */
  stop(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.healthCheckCallbacks = [];
  }

  /**
   * Manual keep-alive ping
   */
  async ping(): Promise<HealthCheckResponse> {
    return this.performHealthCheck();
  }

  /**
   * Pre-warm the backend, including the heavy Service Bus / Foundry path.
   *
   * Unlike a plain health check, this asks the backend to enqueue a warm-up
   * message so the `processChatWork` instance spins up and initializes Foundry
   * before the user sends their first message. Throttled so rapid triggers
   * (app open + input focus) don't flood the queue.
   */
  async prewarm(): Promise<HealthCheckResponse | null> {
    const now = Date.now();
    // Only throttle when a throttle window is configured; otherwise always prewarm.
    if (this.PREWARM_THROTTLE && now - this.lastPrewarmTime < this.PREWARM_THROTTLE) {
      return this.lastHealthCheck;
    }
    this.lastPrewarmTime = now;

    return this.executeOperation(async () => {
      const response = await apiService.get<HealthCheckResponse>('/api/system/keep-alive?prewarm=true');
      this.lastHealthCheck = response;
      this.healthCheckCallbacks.forEach(callback => callback(response));
      return response;
    }, 'prewarm');
  }
}

export const healthService = new HealthService();
